import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Form, Spinner, Alert, Pagination, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import * as mapYourMedsConstant from '../../common/mapYourMedsConstant'

const PAGE_SIZE = 10;

const DayProgramModal = ({ show, onHide, patientId, patientName = 'Patient', day_programs, setDayProgramData }) => {
  const [programs, setPrograms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  // Track which checkboxes are mid-request to prevent double-clicks
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [stateoptions] = useState([{ key: 'Select a state', value: '' },
        ...mapYourMedsConstant.STATE_VALUES]);
  // const { day_programs } = useLocation().state || {};

  // ── Fetch list whenever modal opens ──────────────────────────────────────
  useEffect(() => {
    if (show) {
      fetchPrograms();
      setSearch('');
      setCurrentPage(1);
    }
  }, [show]);

  // ── Reset to page 1 whenever search term changes ──────────────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // ── Derived: filtered + paginated ─────────────────────────────────────────
  const filteredPrograms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
    );
  }, [programs, search]);

  const totalPages = Math.max(1, Math.ceil(filteredPrograms.length / PAGE_SIZE));

  const pagedPrograms = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredPrograms.slice(start, start + PAGE_SIZE);
  }, [filteredPrograms, currentPage]);

  // ── Fetch programs ────────────────────────────────────────────────────────
  // 3. In fetchPrograms, seed is_enrolled from the prop after data arrives
const fetchPrograms = async () => {
  setIsLoading(true);
  setApiError(false);
  try {
    const token = localStorage.getItem('accessToken');
    const { data } = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/day-programs/list`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Normalize: if API returns is_enrolled, prefer it; otherwise fall back to prop
    const enrolledIds = new Set(
      Array.isArray(day_programs) ? day_programs.map(d => d?.id ?? d) : []
    );

    const normalized = (data.data || []).map(p => ({
      ...p,
      is_enrolled: p.is_enrolled ?? enrolledIds.has(p.id)
    }));
    const final = normalized.filter(p => Number(p.is_active) == 1);
    setPrograms(final);
  } catch (err) {
    console.error('Failed to fetch day programs:', err);
    setApiError(true);
    toast.error('Failed to load day programs.');
  } finally {
    setIsLoading(false);
  }
};

  // ── Toggle enroll / unenroll ──────────────────────────────────────────────
  const handleToggle = async (program) => {
    const { id: day_program_id, is_enrolled } = program;

    // Prevent concurrent requests on same row
    if (loadingIds.has(day_program_id)) return;

    const endpoint = is_enrolled
      ? '/day-programs/unenroll-patient'
      : '/day-programs/enroll-patient';

    // Optimistic UI update
    setPrograms((prev) =>
      prev.map((p) =>
        p.id === day_program_id ? { ...p, is_enrolled: !is_enrolled } : p
      )
    );
    setLoadingIds((prev) => new Set(prev).add(day_program_id));

    try {
      const token = localStorage.getItem('accessToken');
      const {data} = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}${endpoint}`,
        { day_program_id, patient_id: patientId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        is_enrolled
          ? `Unenrolled from "${program.name}"`
          : `Enrolled in "${program.name}"`
      );
      setDayProgramData(data.patient_programs);
    } catch (err) {
      console.error('Toggle enrollment failed:', err);
      // Revert optimistic update on failure
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === day_program_id ? { ...p, is_enrolled } : p
        )
      );
      toast.error(
        is_enrolled
          ? `Failed to unenroll from "${program.name}"`
          : `Failed to enroll in "${program.name}"`
      );
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(day_program_id);
        return next;
      });
    }
  };

  // ── Pagination items ──────────────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const delta = 2; // pages to show around current page
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage((p) => p - 1)}
      />
    );

    if (left > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setCurrentPage(1)}>1</Pagination.Item>);
      if (left > 2) items.push(<Pagination.Ellipsis key="ellipsis-left" disabled />);
    }

    for (let page = left; page <= right; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (right < totalPages) {
      if (right < totalPages - 1) items.push(<Pagination.Ellipsis key="ellipsis-right" disabled />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage((p) => p + 1)}
      />
    );

    return (
      <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
        <span className="text-muted fs-11">
          Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredPrograms.length)}–
          {Math.min(currentPage * PAGE_SIZE, filteredPrograms.length)} of {filteredPrograms.length}
        </span>
        <Pagination size="sm" className="mb-0">{items}</Pagination>
      </div>
    );
  };

  // ── Table body ────────────────────────────────────────────────────────────
  const renderTable = () => {
    if (pagedPrograms.length === 0) {
      return (
        <Alert variant="info" className="text-center fw-bold mx-3 mt-3">
          {search.trim() ? `No programs match "${search}"` : 'No day programs available.'}
        </Alert>
      );
    }

    return (
      <Table striped hover size="sm" className="fs-10 mb-0">
        <thead className="bg-200 text-900">
          <tr>
            <th className="text-center px-3" style={{ width: 48 }}>Enrolled</th>
            <th className="px-3">Program Name</th>
            <th className="px-3">Description</th>
            <th className="px-3">Address</th>
            <th className="px-3">City</th>
            <th className="px-3">State</th>
            <th className="px-3">Zip</th>
            <th className="px-3">Phone</th>
            <th className="px-3">Email</th>
          </tr>
        </thead>
        <tbody>
          {pagedPrograms.map((program) => {
            const isPending = loadingIds.has(program.id);
            return (
              <tr
                key={program.id}
                className={isPending ? 'opacity-50' : ''}
                style={{ transition: 'opacity 0.2s' }}
              >
                <td className="text-center px-3 align-middle">
                  {isPending ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                  ) : (
                    <Form.Check
                      type="checkbox"
                      checked={!!program.is_enrolled || day_programs.includes(program.id)}
                      onChange={() => handleToggle(program)}
                      aria-label={`Toggle enrollment for ${program.name}`}
                    />
                  )}
                </td>
                <td className="px-3 align-middle fw-semibold">{program.name}</td>
                <td className="px-3 align-middle text-muted">
                  {program.description || '—'}
                </td>
                <td className="px-3 align-middle text-muted">
                  {(program.address1 && program.address1 + ', ') + (program.address2 && program.address2) || '—'}
                </td>
                <td className="px-3 align-middle text-muted">
                  {program.city || '—'}
                </td>
                <td className="px-3 align-middle text-muted">
                  {stateoptions.find(s => s.value === program.state)?.key }
                </td>
                <td className="px-3 align-middle text-muted">
                  {program.zip || '—'}
                </td>
                <td className="px-3 align-middle text-muted">
                  {program.phone || '—'}
                </td>
                <td className="px-3 align-middle text-muted">
                  {program.email || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted fs-6">Loading day programs…</p>
        </div>
      );
    }

    if (apiError) {
      return (
        <Alert variant="danger" className="text-center fw-bold m-3">
          Failed to load day programs. Please try again.
        </Alert>
      );
    }

    return (
      <>
        {/* Search bar */}
        <div className="px-3 pt-3 pb-2 border-bottom">
          <InputGroup size="sm">
            <InputGroup.Text>
              <FontAwesomeIcon icon="search" className="fs-11 text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by program name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <InputGroup.Text
                style={{ cursor: 'pointer' }}
                onClick={() => setSearch('')}
                title="Clear search"
              >
                <FontAwesomeIcon icon="times" className="fs-11 text-muted" />
              </InputGroup.Text>
            )}
          </InputGroup>
        </div>

        {/* Table */}
        {renderTable()}

        {/* Pagination */}
        {renderPagination()}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">
          Day Programs — {patientName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">{renderBody()}</Modal.Body>
    </Modal>
  );
};

export default DayProgramModal;